/**
 * HmSalesReport — zestawienie „Sprzedaż za okres".
 *
 * Cały stan ekranu jest POCHODNĄ ADRESU: zakres, filtry, wymiar podsumowania i tryb pozycji
 * siedzą w parametrach zakładki. Dzięki temu raport da się odświeżyć, wrócić do niego z innej
 * zakładki i podać komuś linkiem — a zmiana wymiaru to podmiana parametru, nie osobny stan.
 *
 * Podsumowanie PRZELICZAMY U SIEBIE, tą samą funkcją, której używa API (`aggregateSalesReport`
 * z pakietu współdzielonego). Dwa powody: po dociągnięciu szczegółów trzeba policzyć raport od
 * nowa, a przy okazji zmiana wymiaru nie wymaga ponownego pytania Symfonii — dane na ekranie już
 * są, a to samo pytanie potrafi kosztować setki wywołań.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  aggregateSalesReport,
  explodeToPositions,
  HM_SALES_REPORT_DIMENSIONS,
  hmReportDimensionNeedsDetails,
  type HmReportMoney,
  type HmSalesReportDetail,
  type HmSalesReportDimension,
  type HmSalesReportRow,
  mergeDetailsIntoRows,
} from '@saas/shared';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Download, FileSpreadsheet, Printer, RefreshCw, SlidersHorizontal } from 'lucide-react';

import { Button } from '@/components/Button';
import type { ColumnDefinition } from '@/components/configurable-table';
import { ConfigurableTable } from '@/components/configurable-table';
import { Dropdown } from '@/components/Dropdown';
import {
  defaultReportParams,
  SalesReportParamsModal,
} from '@/components/handel-magazyn/SalesReportParamsModal';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import {
  chunkIds,
  downloadCsv,
  fetchReportDetails,
  fetchSalesReport,
  isSalesReportSlug,
  parseSearchParams,
  reportDefaults,
  type SalesReportParams,
  salesReportTitleKey,
  salesReportToCsv,
  toSearchParams,
} from '@/lib/handel-magazyn/reports-api';
import { useActiveCompanyScope } from '@/lib/use-active-company';

const PRINT_PERMISSION = 'handel_magazyn.sales.print';

export function HmSalesReport() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const scope = useActiveCompanyScope();
  const { me } = useTenantAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { report: reportSlug } = useParams<{ report: string }>();
  // Nieznany slug w adresie traktujemy jak zestawienie za okres — adres bywa sklejany ręcznie
  // i nie może kończyć się pustym ekranem.
  const slug = isSalesReportSlug(reportSlug) ? reportSlug : 'sprzedaz-za-okres';
  const reportTitle = t(salesReportTitleKey(slug));

  const companyId = scope.companyId;
  const params = useMemo(() => parseSearchParams(searchParams), [searchParams]);

  const canPrint =
    me?.user.isSuperUser === true || (me?.permissions.includes(PRINT_PERMISSION) ?? false);

  const [paramsOpen, setParamsOpen] = useState(false);
  const [details, setDetails] = useState<HmSalesReportDetail[]>([]);
  const [failed, setFailed] = useState<number[]>([]);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [printAll, setPrintAll] = useState(false);

  // Klucz pamięci podręcznej celowo POMIJA wymiar i tryb pozycji — te zmieniają wyłącznie
  // sposób liczenia, który robimy u siebie. Gdyby wchodziły do klucza, każda zmiana wymiaru
  // pytałaby Symfonię od nowa, a przy dociąganych szczegółach powtarzałaby całe N+1.
  const dataKey = useMemo(() => {
    const sp = toSearchParams(params);
    sp.delete('dim');
    sp.delete('grain');
    sp.delete('preset');
    return sp.toString();
  }, [params]);

  const query = useQuery({
    queryKey: ['handel-magazyn', 'sales-report', companyId, dataKey],
    enabled: !!companyId && !!params.dateFrom && !!params.dateTo,
    retry: false,
    queryFn: ({ signal }) => fetchSalesReport(companyId!, params, signal),
  });

  const report = query.data;

  /**
   * Dociąganie szczegółów paczkami. Sekwencyjnie, a nie równolegle — po drugiej stronie jest
   * Symfonia z limitem sesji, a pasek postępu ma pokazywać prawdę, nie migotać.
   */
  const detailsRunId = useRef(0);
  useEffect(() => {
    setDetails([]);
    setFailed([]);
    setProgress(null);
    if (!report || !companyId) return;
    if (!report.params.details || report.detailsSkipped) return;

    const ids = Array.from(new Set(report.rows.map((r) => r.documentId)));
    if (ids.length === 0) return;

    const runId = ++detailsRunId.current;
    const controller = new AbortController();
    const chunks = chunkIds(ids);
    setProgress({ done: 0, total: ids.length });

    void (async () => {
      const collected: HmSalesReportDetail[] = [];
      const missing: number[] = [];
      let done = 0;
      for (const chunk of chunks) {
        try {
          const res = await fetchReportDetails(companyId, chunk, controller.signal);
          collected.push(...res.details);
          missing.push(...res.failed);
        } catch (err) {
          if (controller.signal.aborted) return;
          // Padnięcie jednej paczki nie unieważnia tego, co już zebraliśmy — raport pokaże,
          // czego brakuje, zamiast zamienić się w komunikat o błędzie.
          missing.push(...chunk);
          const message = isAxiosError(err)
            ? ((err.response?.data as { message?: string } | undefined)?.message ?? undefined)
            : undefined;
          toast.show({
            variant: 'warning',
            title: t('tenant.handelMagazyn.reports.detailsChunkFailed'),
            description: message,
          });
        }
        done += chunk.length;
        if (detailsRunId.current !== runId) return;
        setProgress({ done, total: ids.length });
      }
      if (detailsRunId.current !== runId) return;
      setDetails(collected);
      setFailed(missing);
      setProgress(null);
    })();

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, companyId]);

  /** Wiersze raportu: nagłówki z API, wzbogacone szczegółami i — na życzenie — rozbite na pozycje. */
  const rows = useMemo<HmSalesReportRow[]>(() => {
    if (!report) return [];
    const base = report.buckets ? report.buckets.flatMap((b) => b.rows) : report.rows;
    if (details.length === 0) return base;
    const merged = mergeDetailsIntoRows(base, details);
    return params.positions || isPositionDimension(params.dim)
      ? explodeToPositions(merged, details)
      : merged;
  }, [report, details, params.positions, params.dim]);

  const headerRows = useMemo<HmSalesReportRow[]>(() => {
    if (!report) return [];
    const base = report.buckets ? report.buckets.flatMap((b) => b.rows) : report.rows;
    return details.length === 0 ? base : mergeDetailsIntoRows(base, details);
  }, [report, details]);

  const grain = params.positions || isPositionDimension(params.dim) ? 'position' : 'document';
  const withPositions = grain === 'position';

  const aggregate = useMemo(
    () =>
      aggregateSalesReport({
        rows,
        dimension: params.dim,
        grain,
        documentRows: grain === 'position' ? headerRows : undefined,
      }),
    [rows, params.dim, grain, headerRows],
  );

  const columns = useMemo<ColumnDefinition<HmSalesReportRow>[]>(() => {
    const base: ColumnDefinition<HmSalesReportRow>[] = [
      {
        id: 'buyerName',
        header: t('tenant.handelMagazyn.sales.columns.buyer'),
        accessor: (row) => row.buyerName ?? '—',
        defaultWidth: 260,
        renderCell: (_v: unknown, row: HmSalesReportRow) =>
          row.buyerId != null ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto justify-start px-0 py-0 text-sm font-normal text-text-primary underline-offset-2 hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                updateParams({ buyerId: row.buyerId });
              }}
            >
              {row.buyerName ?? '—'}
            </Button>
          ) : (
            <span className="text-text-primary">{row.buyerName ?? '—'}</span>
          ),
      },
      {
        id: 'documentNumber',
        header: t('tenant.handelMagazyn.columns.documentNumber'),
        accessor: (row) => row.documentNumber,
        defaultWidth: 180,
        renderCell: (_v: unknown, row: HmSalesReportRow) => (
          <Button
            variant="ghost"
            size="sm"
            className="h-auto justify-start px-0 py-0 font-mono text-xs font-normal text-text-primary underline-offset-2 hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/handel-magazyn/sprzedaz/${row.documentId}`);
            }}
          >
            {row.documentNumber}
          </Button>
        ),
      },
      {
        id: 'issueDate',
        header: t('tenant.handelMagazyn.reports.columns.issuedOn'),
        accessor: (row) => row.issueDate?.slice(0, 10) ?? '—',
        defaultWidth: 120,
      },
    ];

    if (withPositions) {
      base.push(
        {
          id: 'productCode',
          header: t('tenant.handelMagazyn.reports.columns.product'),
          accessor: (row) => row.productName ?? row.productCode ?? '—',
          defaultWidth: 240,
        },
        {
          id: 'quantity',
          header: t('tenant.handelMagazyn.reports.columns.quantity'),
          accessor: (row) => row.quantity ?? '',
          align: 'right',
          defaultWidth: 90,
        },
      );
    }

    base.push(
      {
        id: 'currency',
        header: t('tenant.handelMagazyn.columns.currency'),
        accessor: (row) => row.currency,
        defaultWidth: 80,
      },
      {
        id: 'netValue',
        header: t('tenant.handelMagazyn.columns.net'),
        accessor: (row) => row.netValue,
        dataType: 'number',
        align: 'right',
        defaultWidth: 120,
      },
      {
        id: 'vatPln',
        header: t('tenant.handelMagazyn.reports.columns.vatPln'),
        accessor: (row) => row.vatPln,
        dataType: 'number',
        align: 'right',
        defaultWidth: 120,
      },
      {
        id: 'grossValue',
        header: t('tenant.handelMagazyn.columns.gross'),
        accessor: (row) => row.grossValue,
        dataType: 'number',
        align: 'right',
        defaultWidth: 130,
        renderCell: (_v: unknown, row: HmSalesReportRow) => (
          <span className="font-medium text-text-primary">{row.grossValue}</span>
        ),
      },
    );
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, withPositions, navigate]);

  function updateParams(patch: Partial<SalesReportParams>) {
    setSearchParams(toSearchParams({ ...params, ...patch }), { replace: false });
  }

  function changeDimension(dim: HmSalesReportDimension) {
    // Wymiar liczony ze szczegółów sam włącza ich dociąganie — inaczej użytkownik wybrałby
    // podsumowanie, którego raport nie ma z czego policzyć.
    updateParams({ dim, ...(hmReportDimensionNeedsDetails(dim) ? { details: true } : {}) });
  }

  const groups = useMemo(() => {
    if (!aggregate.buckets) return undefined;
    return aggregate.buckets.map((bucket) => ({
      key: bucket.key,
      label: bucketLabel(bucket.label, bucket.labelKey, t),
      count: bucket.count,
      rows: bucket.rows,
      summary: <MoneyLines money={bucket.totals} />,
    }));
  }, [aggregate.buckets, t]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  useEffect(() => {
    // Koszyki startują ROZWINIĘTE — raport Symfonii pokazuje wszystko naraz, a zwijanie jest
    // wygodą, nie punktem wyjścia.
    setExpanded(new Set((aggregate.buckets ?? []).map((b) => b.key)));
  }, [aggregate.buckets]);

  const effectiveExpanded = printAll
    ? new Set((aggregate.buckets ?? []).map((b) => b.key))
    : expanded;

  /** Wydruk: najpierw rozwiń wszystko, dopiero potem drukuj — inaczej zwinięte koszyki znikną. */
  function print() {
    setPrintAll(true);
    requestAnimationFrame(() => {
      window.print();
      setPrintAll(false);
    });
  }

  function exportCsv() {
    const sections = aggregate.buckets
      ? aggregate.buckets.map((b) => ({
          label: bucketPlainLabel(b.label, b.labelKey, t),
          rows: b.rows,
          totals: b.totals,
        }))
      : [{ label: null, rows: aggregate.rows, totals: aggregate.grandTotals }];
    const csv = salesReportToCsv({
      headers: columns.map((c) => String(c.header)),
      sections,
      grandTotals: aggregate.grandTotals,
      totalsLabel: t('tenant.handelMagazyn.reports.grandTotal'),
      withPositions,
    });
    downloadCsv(`sprzedaz-${params.dateFrom}_${params.dateTo}.csv`, csv);
  }

  const errorInfo = useMemo(() => {
    if (!isAxiosError(query.error)) return null;
    const data = query.error.response?.data as { error?: string; message?: string } | undefined;
    return data?.message ?? data?.error ?? null;
  }, [query.error]);

  return (
    <div className="p-4">
      <div className="print-area">
        <div className="mb-3 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-text-primary">{reportTitle}</h1>
            <p className="text-sm text-text-secondary">
              {t('tenant.handelMagazyn.reports.rangeLabel', {
                from: params.dateFrom,
                to: params.dateTo,
              })}
              {report ? ` · ${report.integrationName}` : ''}
            </p>
          </div>
          <div className="no-print flex items-center gap-1">
            <Button variant="secondary" size="sm" onClick={() => setParamsOpen(true)}>
              <SlidersHorizontal className="mr-1.5 h-4 w-4" />
              {t('tenant.handelMagazyn.reports.changeParams')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void query.refetch()}
              title={t('tenant.handelMagazyn.refresh')}
            >
              <RefreshCw className={`h-4 w-4 ${query.isFetching ? 'animate-spin' : ''}`} />
            </Button>
            {canPrint && (
              <>
                <Button variant="secondary" size="sm" onClick={exportCsv} disabled={!report}>
                  <Download className="mr-1.5 h-4 w-4" />
                  {t('tenant.handelMagazyn.toolbar.export')}
                </Button>
                <Button variant="secondary" size="sm" onClick={print} disabled={!report}>
                  <Printer className="mr-1.5 h-4 w-4" />
                  {t('tenant.handelMagazyn.toolbar.print')}
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="no-print mb-3 flex items-center gap-2">
          <span className="text-sm text-text-secondary">
            {t('tenant.handelMagazyn.reports.summarizeBy')}
          </span>
          <div className="w-64">
            <Dropdown
              value={params.dim}
              onChange={(value) => changeDimension(value as HmSalesReportDimension)}
              options={HM_SALES_REPORT_DIMENSIONS.map((dim) => ({
                value: dim,
                label: t(`tenant.handelMagazyn.reports.dimension.${dim}`),
              }))}
              data-testid="hm-report-dimension-live"
            />
          </div>
          {params.buyerId != null && (
            <Button variant="ghost" size="sm" onClick={() => updateParams({ buyerId: null })}>
              {t('tenant.handelMagazyn.reports.clearBuyerFilter')}
            </Button>
          )}
        </div>

        {errorInfo && (
          <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
            {errorInfo}
          </div>
        )}

        {report?.detailsSkipped && (
          <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {t('tenant.handelMagazyn.reports.detailsSkipped')}
          </div>
        )}
        {report?.truncated && (
          <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {t('tenant.handelMagazyn.reports.truncated')}
          </div>
        )}
        {failed.length > 0 && (
          <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {t('tenant.handelMagazyn.reports.detailsFailed', { count: failed.length })}
          </div>
        )}
        {progress && (
          <div
            className="no-print mb-2 rounded-md border border-border-primary bg-bg-secondary px-3 py-2 text-xs text-text-secondary"
            data-testid="hm-report-progress"
          >
            {t('tenant.handelMagazyn.reports.detailsProgress', {
              done: progress.done,
              total: progress.total,
            })}
          </div>
        )}

        {/* Suma całkowita NAD tabelą — tabela nie ma stopki na poziomie całości, a suma jest
            pierwszą rzeczą, której się tu szuka. */}
        <div
          className="mb-2 flex flex-wrap items-center gap-x-6 gap-y-1 rounded-md border border-border-primary bg-bg-secondary px-3 py-2"
          data-testid="hm-report-totals"
        >
          <span className="text-sm font-semibold text-text-primary">
            {t('tenant.handelMagazyn.reports.grandTotal')}
          </span>
          <span className="text-xs text-text-secondary">
            {withPositions
              ? t('tenant.handelMagazyn.reports.countsWithPositions', {
                  documents: aggregate.documentCount,
                  positions: aggregate.positionCount ?? 0,
                })
              : t('tenant.handelMagazyn.reports.counts', { documents: aggregate.documentCount })}
          </span>
          <MoneyLines money={aggregate.grandTotals} />
          {aggregate.totalsMismatch && (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-900">
              {t('tenant.handelMagazyn.reports.totalsMismatch')}
            </span>
          )}
        </div>

        <ConfigurableTable<HmSalesReportRow>
          tableId="hm_sales_report"
          columns={columns}
          data={aggregate.buckets ? [] : aggregate.rows}
          groups={groups}
          expandedGroupKeys={groups ? effectiveExpanded : undefined}
          onToggleGroup={
            groups
              ? (key) =>
                  setExpanded((prev) => {
                    const next = new Set(prev);
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    return next;
                  })
              : undefined
          }
          isLoading={query.isLoading}
          getRowId={(row) => `${row.documentId}-${row.positionNo ?? 0}`}
          persistConfig={false}
          configurable={false}
          emptyIcon={FileSpreadsheet}
          emptyTitle={t('tenant.handelMagazyn.reports.empty')}
          // Suma stoi też POD tabelą, w tych samych kolumnach — tak wygląda raport z Symfonii
          // i tak czyta się go na wydruku. Pasek nad tabelą zostaje, bo przy długiej liście
          // suma jest wtedy widoczna bez przewijania na dół.
          footerLabel={t('tenant.handelMagazyn.reports.grandTotal')}
          footerCells={(column) => {
            // Mieszanka walut daje kilka sum — w wierszu pokazujemy każdą w osobnej linii.
            const money = aggregate.grandTotals;
            if (money.length === 0) return null;
            if (column.id === 'currency') {
              return money.map((m) => <div key={m.currency}>{m.currency}</div>);
            }
            const field =
              column.id === 'netValue'
                ? 'net'
                : column.id === 'vatPln'
                  ? 'vatPln'
                  : column.id === 'grossValue'
                    ? 'gross'
                    : null;
            if (!field) return null;
            return money.map((m) => <div key={m.currency}>{m[field]}</div>);
          }}
          testIdPrefix="hm-sales-report"
          stickyHeader
          compact
        />
      </div>

      <SalesReportParamsModal
        open={paramsOpen}
        companyId={companyId}
        initial={params.dateFrom ? params : reportDefaults(slug, defaultReportParams())}
        title={reportTitle}
        onClose={() => setParamsOpen(false)}
        onSubmit={(next) => {
          setParamsOpen(false);
          setSearchParams(toSearchParams(next), { replace: false });
        }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ pomocnicze */

function isPositionDimension(dim: HmSalesReportDimension): boolean {
  return dim === 'product' || dim === 'vatRate';
}

function bucketPlainLabel(
  label: string | null,
  labelKey: string | null,
  t: (key: string) => string,
): string {
  if (labelKey) return t(`tenant.handelMagazyn.status.${labelKey}`);
  return label ?? t('tenant.handelMagazyn.reports.noValue');
}

function bucketLabel(label: string | null, labelKey: string | null, t: (key: string) => string) {
  const text = bucketPlainLabel(label, labelKey, t);
  return <span className="font-medium text-text-primary">{text}</span>;
}

/** Kwoty per waluta — osobna linia na walutę, bo euro nie sumuje się ze złotówkami. */
function MoneyLines({ money }: { money: HmReportMoney[] }) {
  const { t } = useTranslation();
  if (money.length === 0) return <span className="text-xs text-text-tertiary">—</span>;
  return (
    <span className="flex flex-wrap items-center gap-x-4 gap-y-0.5">
      {money.map((m) => (
        <span key={m.currency} className="text-xs text-text-secondary">
          <span className="font-mono text-text-primary">{m.gross}</span> {m.currency}
          <span className="ml-2 text-text-tertiary">
            {t('tenant.handelMagazyn.columns.net')} {m.net} ·{' '}
            {t('tenant.handelMagazyn.reports.columns.vatPln')} {m.vatPln}
          </span>
        </span>
      ))}
    </span>
  );
}

export default HmSalesReport;
